// ─── Content Sanitizer ────────────────────────────────────────────────────────
// Runs on chapter HTML before export. Fixes streaming artifacts, broken
// sentences, markdown leakage, duplicate structure, and AI generation noise.

// ── Broken sentence patterns ─────────────────────────────────────────────────

// "supplemented by ." / "ranges from ." / "gathered for this study..."
const BROKEN_SENTENCE_RE = /\b(\w[\w\s,;-]{2,})\s+(by|from|to|for|with|of|in|on|at|through|via)\s+\./g
// The closing tag is part of the match, so it has to be put back. Replacing the
// whole match with "." deleted the </p>, leaving the paragraph unclosed and the
// rest of the chapter reparented into it by the HTML parser.
const TRAILING_PREPOSITION_RE = /\s*\b(?:by|from|to|for|with|of|in|on|at|through|via)\s*\.\s*(<\/p>|$)/gi
const DOUBLE_PERIOD_RE = /\.{2,}(?!\d)/g

// Streaming join artifacts: line ends mid-phrase, next line continues awkwardly
const STREAM_JOIN_RE = /([a-z,;])\n([a-z])/g

// "References to be compiled in thesis bibliography..." placeholder text
const REFERENCES_PLACEHOLDER_RE = /References?\s+to\s+be\s+compiled[^<.]*[.<]/gi
// Only ever a bracketed placeholder token. This previously made the brackets
// optional and matched case-insensitively with no word boundaries, so it ate
// the middle of ordinary words wherever one of these strings happened to sit
// inside them: "inserted" became "ed", "insertion" became "ion",
// "Placeholders" became "s". It also deleted the unbracketed phrases outright,
// which are perfectly normal academic prose — "these findings will be
// discussed in Chapter 5" lost its verb and read "these findings in Chapter
// 5". That is the broken-word corruption seen in exported chapters, and it ran
// on every chapter on the way to DOCX/PDF.
//
// A placeholder is a bracketed token and nothing else. Prose is left alone.
const AI_FILLER_RE = /\[\s*(?:INSERT|TODO|TBD|TO_VERIFY|PLACEHOLDER)[^\]]*\]/gi
const ELLIPSIS_PLACEHOLDER_RE = /\.\.\.\s*(?=<\/p>|$)/gm

// ── HTML structure cleanup ────────────────────────────────────────────────────

// Mermaid blocks that leaked into export HTML (pre.mermaid)
const RAW_MERMAID_RE = /<pre[^>]*class="mermaid"[^>]*>[\s\S]*?<\/pre>/gi

// Duplicate <hr> tags
const DOUBLE_HR_RE = /(<hr\s*\/?>)\s*(<hr\s*\/?>)+/gi

// Empty paragraphs (multiple in a row → at most one)
const EMPTY_PARA_RE = /(<p[^>]*>\s*<\/p>\s*){2,}/gi

// Double chapter headings: e.g. <h1>Chapter 3: Foo</h1> immediately followed
// by another <h1> or an exact-match text line
const DOUBLE_HEADING_RE = /(<h1[^>]*>[^<]+<\/h1>)\s*(<h1[^>]*>[^<]+<\/h1>)/gi

// Raw markdown headings that leaked into prose: "## 1.3" inside a <p>
const INLINE_MD_HEADING_RE = /<p[^>]*>(#{1,4})\s+([^<]+)<\/p>/gi

// Orphaned anchor tokens left by streaming chunking
const ORPHAN_ANCHOR_RE = /&lt;CHAPTER_\w+&gt;/g

// ── Em-dash budget ────────────────────────────────────────────────────────────
// Generated chapters ran at 13.68 em-dashes per 1,000 words against an academic
// norm of 1–2 — roughly one every three sentences, and the single most visible
// tell that the prose was machine-written. The prompts now cap this at source;
// this pass exists for content generated before that change.
//
// The target is a budget, not eradication: prose with zero em-dashes reads just
// as uniform as prose that leans on them. One is kept per KEEP_EVERY paragraphs.

const KEEP_EVERY = 5

// Marks a dash the pass has decided to leave alone, so later passes skip it.
const KEPT = '\u0001'

// A dash sitting between two digits is a numeric range (1995—2020), not prose.
const NUMERIC_RANGE = /\d\s*—\s*\d/

// Prose that must keep its dashes verbatim: headings carry "Figure 2.1 — Title"
// captions, and figure/table markup is generated, not written.
const PROTECTED_BLOCK = /<(?:h[1-6]|figure|figcaption|table|pre|code)\b[\s\S]*?<\/(?:h[1-6]|figure|figcaption|table|pre|code)>/gi

// A parenthetical: " — ... — " with no sentence end between the two dashes.
const PAIRED_DASH = /\s+—\s+([^—.!?]{1,120}?)\s+—\s+/g

// A single dash mid-sentence.
const SINGLE_DASH = /\s+—\s+/g

// What follows an explanatory dash often reads better after a colon: a list, or
// a clause opening with a determiner/negation that expands on what came before.
const WANTS_COLON = /^(?:not\b|no\b|the\b|a\b|an\b|one\b|two\b|three\b|those\b|these\b|both\b)/i

function rewriteDashesInText(text, budget) {
  if (!text.includes('—')) return { text, budget }

  let out = text
  let left = budget

  // A dash we decide to keep is parked as KEPT so the later passes cannot
  // rewrite it. Without this, a kept parenthetical spent the budget and was then
  // taken apart dash-by-dash by the single-dash pass anyway.
  const keep = (s) => s.replace(/—/g, KEPT)

  // Parentheticals first: " x — y — z " → " x, y, z ". Handling these before
  // single dashes matters, otherwise the opening dash is consumed alone and the
  // closing one is left stranded.
  out = out.replace(PAIRED_DASH, (match, inner) => {
    if (NUMERIC_RANGE.test(match)) return keep(match)
    if (left > 0) { left--; return keep(match) }
    return `, ${inner}, `
  })

  out = out.replace(SINGLE_DASH, (match, offset, full) => {
    // Widen far enough to see a digit on both sides: the match itself is the
    // dash plus its surrounding spaces, so a 2-char lookbehind misses "2019 - 2023".
    const around = full.slice(Math.max(0, offset - 4), offset + match.length + 4)
    if (NUMERIC_RANGE.test(around)) return keep(match)
    if (left > 0) { left--; return keep(match) }
    const rest = full.slice(offset + match.length)
    return WANTS_COLON.test(rest) ? ': ' : ', '
  })

  return { text: out.split(KEPT).join('—'), budget: left }
}

/**
 * Reduce em-dash density in prose while leaving headings, captions, figures,
 * tables, code and numeric ranges untouched.
 */
export function normalizeDashes(html) {
  if (!html || !html.includes('—')) return html || ''

  // Lift protected blocks out, rewrite what remains, then put them back.
  // Sentinel is a control character so it can never collide with real prose:
  // a bare number placeholder would match "in 5 years" on the way back.
  const stash = []
  let out = html.replace(PROTECTED_BLOCK, (block) => {
    stash.push(block)
    return `\u0000${stash.length - 1}\u0000`
  })

  // Walk paragraph by paragraph so the budget is spread across the chapter
  // rather than spent entirely in the opening section.
  let paraIndex = 0
  let budget = 0
  out = out.replace(/<p([^>]*)>([\s\S]*?)<\/p>/gi, (match, attrs, inner) => {
    if (paraIndex++ % KEEP_EVERY === 0) budget = 1
    const result = rewriteDashesInText(inner, budget)
    budget = result.budget
    return `<p${attrs}>${result.text}</p>`
  })

  // Content with no paragraph markup at all (plain text or fragments).
  if (paraIndex === 0) out = rewriteDashesInText(out, 1).text

  return out.replace(/\u0000(\d+)\u0000/g, (_, i) => stash[Number(i)])
}

// ── Leaked generator markers ─────────────────────────────────────────────────
// The figure pipeline converts [FIGURE_START]…[FIGURE_END] blocks into tokens,
// but interrupted saves and older content left the raw markers in place, so
// readers saw "[TABLE_START] TITLE: Table 5.8 — …" printed above an otherwise
// correct table. The title is real content, so keep it as a caption and drop
// only the marker.

const LEAKED_TITLE_RE = /\[(?:FIGURE|TABLE)_START\]\s*(?:TYPE:\s*\w+)?\s*TITLE:\s*([^<\n]*)/gi
const LEAKED_MARKER_RE = /\[(?:FIGURE|TABLE)_(?:START|END)\]\s*(?:TYPE:\s*\w+)?\s*(?:CODE:|CAPTION:)?/gi

function stripLeakedFigureMarkers(html) {
  if (!html || !html.includes('_START]') && !html.includes('_END]')) return html
  return html
    .replace(LEAKED_TITLE_RE, (_, title) =>
      `<span class="fig-title" style="font-weight:bold">${title.trim()}</span>`)
    .replace(LEAKED_MARKER_RE, '')
}

// ── Text normalisation helpers ────────────────────────────────────────────────

function fixBrokenSentences(text) {
  // "supplemented by ." → remove the orphaned preposition + period
  return text
    .replace(TRAILING_PREPOSITION_RE, '.$1')
    .replace(DOUBLE_PERIOD_RE, '.')
}

function fixMarkdownHeadingsInParagraphs(html) {
  // <p>## 1.3 Section Title</p> → <h3>1.3 Section Title</h3>
  return html.replace(INLINE_MD_HEADING_RE, (_, hashes, title) => {
    const level = Math.min(hashes.length + 1, 6) // ## → h3, ### → h4, etc.
    return `<h${level}>${title.trim()}</h${level}>`
  })
}

// ── Main export function ──────────────────────────────────────────────────────

/**
 * Sanitize chapter HTML for export.
 * Input: raw TipTap HTML stored in chapters.content
 * Output: clean HTML safe for DOCX/HTML export
 */
export function sanitizeChapterContent(html) {
  if (!html) return ''

  let out = html

  // 1. Raw mermaid leakage (should have been replaced by figure tokens, but
  //    interrupted saves/older content can still have it) — render it as a
  //    visible labelled block instead of silently deleting a real diagram.
  out = out.replace(RAW_MERMAID_RE, (block) => {
    const code = block.replace(/^<pre[^>]*>/i, '').replace(/<\/pre>$/i, '')
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
    return `<figure class="thesis-fig"><pre class="mermaid-src">${code.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre></figure>`
  })

  // 2. Fix inline markdown headings inside <p> tags
  out = fixMarkdownHeadingsInParagraphs(out)

  // 3. Remove AI placeholder text
  out = out.replace(REFERENCES_PLACEHOLDER_RE, '')
  out = out.replace(AI_FILLER_RE, '')
  out = out.replace(ELLIPSIS_PLACEHOLDER_RE, '.')
  out = out.replace(ORPHAN_ANCHOR_RE, '')

  // 4. Fix broken sentence endings (prepositions dangling before period)
  out = fixBrokenSentences(out)

  // 4a. Drop leaked [FIGURE_START]/[TABLE_START] generator markers
  out = stripLeakedFigureMarkers(out)

  // 4b. Bring em-dash density back to an academic range
  out = normalizeDashes(out)

  // 5. Collapse duplicate structural elements
  out = out.replace(DOUBLE_HR_RE, '<hr>')
  out = out.replace(DOUBLE_HEADING_RE, '$1') // keep first heading only
  out = out.replace(EMPTY_PARA_RE, '<p></p>')

  // 6. Normalise whitespace around block elements
  out = out.replace(/\s{3,}/g, ' ')

  return out.trim()
}

// ── Front matter sanitizer ────────────────────────────────────────────────────

const FM_PLACEHOLDERS = [
  /\[Month\]/g,
  /\[Year\]/g,
  /\[Faculty\/Department\]/g,
  /\[Faculty\]/g,
  /\[Department\]/g,
  /\[Examiner Name\]/g,
  /\[Designation\]/g,
  /\[Institution\]/g,
  /\[City\]/g,
  /\[Student Name\]/g,
  /\[Supervisor Name\]/g,
  /\[University Name\]/g,
  /\[Thesis Title\]/g,
  /\[Reg\. No\.\]/g,
  /\[Specialization\]/g,
]

/**
 * Check if a string contains un-interpolated front matter placeholders.
 * Returns array of found placeholder strings.
 */
export function findFrontMatterPlaceholders(text) {
  if (!text) return []
  const found = []
  for (const re of FM_PLACEHOLDERS) {
    const m = text.match(re)
    if (m) found.push(...m)
  }
  return [...new Set(found)]
}

/**
 * Find placeholder tokens in chapter content (HTML).
 * Returns array of { token, context } objects.
 */
export function findContentPlaceholders(html) {
  if (!html) return []
  const text  = html.replace(/<[^>]+>/g, ' ')
  const found = []

  const patterns = [
    /\[PLACEHOLDER\]/gi,
    /\[TODO[^\]]*\]/gi,
    /\[INSERT[^\]]*\]/gi,
    /\[TBD[^\]]*\]/gi,
    /\[TO_VERIFY[^\]]*\]/gi,
    /XXXXX+/g,
  ]

  for (const re of patterns) {
    let m
    while ((m = re.exec(text)) !== null) {
      const start   = Math.max(0, m.index - 40)
      const end     = Math.min(text.length, m.index + m[0].length + 40)
      const context = '…' + text.slice(start, end).trim() + '…'
      found.push({ token: m[0], context })
    }
  }

  return found
}

/**
 * Detect broken/incomplete sentences in HTML content.
 * Returns array of { sentence, context } for each suspicious pattern.
 */
export function findBrokenSentences(html) {
  if (!html) return []
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ')
  const found = []

  const patterns = [
    { re: /\b\w[\w\s,;-]{2,}\s+(?:by|from|to|for|with|of|in|on|at)\s+\./g, label: 'dangling preposition' },
    { re: /The\s+\w+\s+(?:gathered|collected|obtained|conducted)\s+for\s+this\s+study[^.]*\./g, label: 'incomplete clause' },
    { re: /,\s+including\s+(?:a|the|an)?\s*\./g, label: 'truncated list' },
    { re: /which\s+(?:include|involve|contain|comprise)s?\s*\./g, label: 'truncated which-clause' },
  ]

  for (const { re, label } of patterns) {
    let m
    while ((m = re.exec(text)) !== null) {
      const start   = Math.max(0, m.index - 20)
      const end     = Math.min(text.length, m.index + m[0].length + 20)
      found.push({ sentence: m[0], context: text.slice(start, end).trim(), label })
    }
  }

  return found
}
